import { selectMasterTransactionsTableCurrentTab } from './../reducers/index';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import { StatusViewService } from '../../shared/services/status-view.service';
import { ActivityReportTableActions , MasterStatusViewActions, MasterTransactionsTableActions } from '../actions';
import { selectMasterAccount } from '../../account-info-header/reducers';
import { ServiceError } from '../models/shared/ServiceError';
import { ActivityReportResponse } from '../models/master-status-view/ActivityReportResponse';
import { JsonApiListWrap } from '../../shared/models/JsonApiWrap';

@Injectable()
export class ActivityReportEffects {
  loadMasterAccountInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ActivityReportTableActions .getActivityReports,
        MasterStatusViewActions.refreshMaster,
        MasterTransactionsTableActions.currentTabUpdated,
      ),
      concatLatestFrom(() => [
        this.store.select(selectMasterAccount),
        this.store.select(selectMasterTransactionsTableCurrentTab),
      ]),
      switchMap(([action, masterAccountId, currentTab]) => {
        masterAccountId = masterAccountId.padStart(8, '0');
        if (currentTab === 'Activity Report') {
          return this.statusViewService
            .getActivityReports(masterAccountId)
            .pipe(
              map((response: JsonApiListWrap<ActivityReportResponse>) => {
                if (response?.data.length >= 1) {
                  return ActivityReportTableActions .callActivityReportsSuccess(
                    response
                  );
                } else {
                  return ActivityReportTableActions .hasEmptyActivityReportResponse();
                }
              }),
              catchError((err: ServiceError) => {
                return of(ActivityReportTableActions .callActivityReportsFailure(err));
              })
            )
        }
        return EMPTY;
      })
    )
  );

  downloadActivityReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ActivityReportTableActions .downloadActivityReportClicked
      ),
      concatLatestFrom(() => [
        this.store.select(selectMasterAccount),
      ]),
      switchMap(([action, masterAccountId]) => {
        const { activityReportId, interimSequenceId } = action;
        masterAccountId = masterAccountId.padStart(8, '0');
        return this.statusViewService
          .downloadActivityReport(masterAccountId, activityReportId, interimSequenceId)
          .pipe(
            map((response: Blob) => {
              return ActivityReportTableActions .downloadActivityReportSuccess({ file: response });
            }),
            catchError((err: ServiceError) => {
              return of(ActivityReportTableActions .downloadActivityReportFailure(err));
            })
          );
      })
    )
  );

  generateActivityReportCsv$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ActivityReportTableActions .downloadActivityReportSuccess),
        tap(async action => {
          const blob = action.file;
          const redesignCompliantNames = {
            MoneyLink: 'ACH Transaction',
            MoneyLinks: 'ACH transactions',
            Moneylink: 'ACH Transaction',
            Journal: 'Internal Transfer',
            Journals: 'Internal Transfer',
          };
          const newBlob = await this.replaceWordsInBlob(blob, redesignCompliantNames)
          const url = window.URL.createObjectURL(newBlob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'MoveMoneyActivityReport.csv';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        })
      ),
    { dispatch: false }
  );

  async replaceWordsInBlob(blob: Blob, replacements: Record<string, string>): Promise<Blob> {
    return blob.text().then(text => {
      let newText = text;
      for (const [oldWord, newWord] of Object.entries(replacements)) {
        const regex = new RegExp(`\\b${oldWord}\\b`, 'g');
        newText = newText.replace(regex, newWord);
      }
      return new Blob([newText], { type: blob.type });
    });
  }

  constructor(
    private actions$: Actions,
    private statusViewService: StatusViewService,
    private store: Store
  ) {}
}